import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { LogIn, Search } from 'lucide-react'
import { Bone, Input, Select } from '@/components/ui'
import { Avatar } from '@/components/common/Avatar'
import { cn } from '@/lib/cn'
import type { OrganizationDashboard, OrganizationDashboardRosterRow } from '@/types/dashboard.types'
import { DashboardDetailModal, DashboardTile } from './DashboardTile'
import { formatClock, formatMinutes, formatNumber } from './dashboardFormat'

const CARD_ROWS = 9

function ClockInRow({
  row,
  timezone,
  detailed = false,
}: {
  row: OrganizationDashboardRosterRow
  timezone?: string
  detailed?: boolean
}): React.ReactElement {
  const late = row.status === 'late'
  return (
    <li className="flex items-center gap-3 py-2.5">
      <Avatar name={row.name} size="md" tone="soft" />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-gray-900 dark:text-gray-100">{row.name}</p>
        <p className="truncate text-xs text-gray-500 dark:text-gray-400">
          {row.companyName}
          {detailed && row.employeeId ? ` · ${row.employeeId}` : ''}
          {late && row.lateByMinutes > 0 ? ` · ${formatMinutes(row.lateByMinutes)} late` : ''}
        </p>
      </div>
      {detailed ? (
        <span className="hidden text-xs text-gray-500 tabular-nums dark:text-gray-400 sm:inline">
          {row.isClockedIn ? 'Working' : `Out ${formatClock(row.clockOut, timezone)}`}
        </span>
      ) : null}
      <span
        className={cn(
          'inline-flex shrink-0 items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold tabular-nums',
          late
            ? 'bg-amber-50 text-amber-800 dark:bg-amber-400/10 dark:text-amber-300'
            : 'bg-primary/[0.07] text-primary dark:bg-sky-400/10 dark:text-sky-300'
        )}
      >
        {row.isClockedIn ? (
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" aria-label="Clocked in now" />
        ) : null}
        {formatClock(row.clockIn, timezone)}
      </span>
    </li>
  )
}

/** Today's latest clock-ins across every visible company. */
export function RecentClockInsCard({
  data,
  isLoading,
  onExpand,
  className,
}: {
  data: OrganizationDashboard | undefined
  isLoading: boolean
  onExpand: () => void
  className?: string
}): React.ReactElement {
  const rows = data?.recentClockIns ?? []
  const clockedIn = data?.summary.clockedInNow ?? 0

  return (
    <DashboardTile
      title="Recent clock-ins"
      subtitle={
        data
          ? data.isLive === false
            ? `${formatNumber(rows.length)} clocked in that day`
            : `${formatNumber(clockedIn)} working right now · ${formatNumber(rows.length)} in today`
          : 'Today'
      }
      icon={LogIn}
      onExpand={onExpand}
      className={className}
      bodyClassName="px-5"
      footer={
        <Link
          to="/attendance"
          className="flex h-9 w-full items-center justify-center rounded-lg border border-accent/40 text-sm font-medium text-[#d92d50] transition-colors hover:bg-accent/5 dark:border-rose-400/40 dark:text-rose-300 dark:hover:bg-rose-400/10"
        >
          View attendance log
        </Link>
      }
    >
      {isLoading ? (
        <ul className="space-y-4 py-2">
          {Array.from({ length: 5 }, (_, i) => (
            <li key={i} className="flex items-center gap-3">
              <Bone className="h-8 w-8 rounded-full" />
              <div className="flex-1 space-y-1.5">
                <Bone className="h-3 w-28 rounded" />
                <Bone className="h-2.5 w-20 rounded" />
              </div>
              <Bone className="h-6 w-16 rounded-md" tint />
            </li>
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <div className="grid h-full min-h-48 place-items-center text-center text-sm text-gray-500 dark:text-gray-400">
          <p className="max-w-[16rem]">
            {data?.recentClockIns
              ? data.isLive === false
                ? 'No one clocked in that day.'
                : 'No one has clocked in yet today. Clock-ins land here as shifts start.'
              : 'Clock-ins will show here once the server is updated.'}
          </p>
        </div>
      ) : (
        <ul className="divide-y divide-gray-100 dark:divide-gray-700/70">
          {rows.slice(0, CARD_ROWS).map((row) => (
            <ClockInRow
              key={`${row.companyId}:${row.userId}`}
              row={row}
              timezone={data?.timezone}
            />
          ))}
        </ul>
      )}
    </DashboardTile>
  )
}

export function RecentClockInsModal({
  open,
  data,
  onClose,
}: {
  open: boolean
  data: OrganizationDashboard | undefined
  onClose: () => void
}): React.ReactElement | null {
  const [search, setSearch] = useState('')
  const [companyId, setCompanyId] = useState('all')
  const rows = useMemo(() => data?.recentClockIns ?? [], [data?.recentClockIns])

  const companyOptions = useMemo(() => {
    const names = new Map(rows.map((row) => [row.companyId, row.companyName]))
    return [
      { value: 'all', label: 'All companies' },
      ...Array.from(names, ([value, label]) => ({ value, label })).sort((a, b) =>
        a.label.localeCompare(b.label)
      ),
    ]
  }, [rows])

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return rows.filter(
      (row) =>
        (companyId === 'all' || row.companyId === companyId) &&
        (q === '' ||
          row.name.toLowerCase().includes(q) ||
          (row.employeeId?.toLowerCase().includes(q) ?? false))
    )
  }, [rows, search, companyId])

  if (!open) return null

  return (
    <DashboardDetailModal
      open
      onClose={onClose}
      title={data?.isLive === false ? 'Clock-ins that day' : 'Clock-ins today'}
      subtitle="Latest first. A green dot means the session is still open."
      toolbar={
        <>
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400"
              aria-hidden
            />
            <Input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search people…"
              aria-label="Search people"
              className="h-9 w-52 pl-8"
            />
          </div>
          <div className="w-48">
            <Select
              options={companyOptions}
              value={companyId}
              onChange={(v) => setCompanyId(v || 'all')}
              clearable={false}
            />
          </div>
        </>
      }
    >
      {filtered.length === 0 ? (
        <p className="py-16 text-center text-sm text-gray-500 dark:text-gray-400">
          {rows.length === 0
            ? 'No one has clocked in yet today.'
            : 'No clock-ins match these filters.'}
        </p>
      ) : (
        <ul className="grid gap-x-8 divide-y divide-gray-100 dark:divide-gray-700/70 lg:grid-cols-2 lg:divide-y-0 [&>li]:border-gray-100 lg:[&>li]:border-b dark:[&>li]:border-gray-700/70">
          {filtered.map((row) => (
            <ClockInRow
              key={`${row.companyId}:${row.userId}`}
              row={row}
              timezone={data?.timezone}
              detailed
            />
          ))}
        </ul>
      )}
    </DashboardDetailModal>
  )
}
